import type {
  CampaignTemplatesQueryResponse,
  CampaignTemplatesQueryVariables,
} from '~/api/graphql/queries/campaignTemplates.generated';
import type { Campaign, CampaignType, Variant } from '~/types/graphql';
import type { InfiniteData } from '@tanstack/react-query';

import { useInfiniteCampaignTemplates } from './useInfiniteCampaignTemplates';
import { refetchAllPages, useFetchAllPages } from '~/hooks';
import { captureSentryError } from '~/exceptions/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { useCampaignStore } from '~/modules/campaign';
import { useShopStore } from '~/stores/shop';
import { sortByConditions } from '~/utils';

// Every CampaignType keeps its origin variant first -- callers that read
// `variants[0]` positionally (instead of checking `isOrigin`) rely on it.
const sortCampaignVariantsByOrigin = (campaign: Campaign): Campaign => ({
  ...campaign,
  variants: sortByConditions<Variant>((campaign.variants as Variant[]) || [], [
    { attr: 'isOrigin' },
  ]),
});

const flattenNodes = (data: InfiniteData<CampaignTemplatesQueryResponse> | undefined): Campaign[] =>
  (data?.pages ?? [])
    .flatMap((page) => page?.campaigns?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is Campaign => !!node)
    .map(sortCampaignVariantsByOrigin);

const hasMorePages = (data: InfiniteData<CampaignTemplatesQueryResponse> | undefined): boolean =>
  !!data?.pages.at(-1)?.campaigns?.pageInfo?.hasNextPage;

export interface IUseQueryCampaignsByTypeProps {
  type: CampaignType;
  queryVariables: CampaignTemplatesQueryVariables;
  isQueried: boolean;
  setIsQueried: (value: boolean) => void;
  getIsQueried: () => boolean;
  sentrySource: string;
  onLoaded?: (campaigns: Campaign[]) => void;
}

export interface IUseQueryCampaignsByTypeReturn {
  isFetching: boolean;
  handleRefetch: () => Promise<void>;
}

// Shared by every CampaignType's bulk loader (Template, Price, ...) -- each
// wrapper only supplies its own where-filter and its own isQueryCampaign*
// store flag/guard, keeping their per-type side effects (e.g. variant sync)
// out of this hook.
export const useQueryCampaignsByType = ({
  type,
  queryVariables,
  isQueried,
  setIsQueried,
  getIsQueried,
  sentrySource,
  onLoaded,
}: IUseQueryCampaignsByTypeProps): IUseQueryCampaignsByTypeReturn => {
  const shopInfo = useShopStore((state) => state.shopInfo);
  const storeCampaigns = useCampaignStore((state) => state.campaigns);
  const setCampaignsByType = useCampaignStore((state) => state.setCampaignsByType);

  const queryClient = useQueryClient();

  const enabled = !!shopInfo?.shopID && !!shopInfo.token && (!storeCampaigns?.length || !isQueried);

  const { data, refetch, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage, isError } =
    useInfiniteCampaignTemplates(queryVariables, {
      enabled,
      getNextPageParam: (lastPage) => {
        const pageInfo = lastPage?.campaigns?.pageInfo;
        return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined;
      },
      // Mount in many non-Campaigns contexts (modals, reports, orders) so we
      // just log to Sentry and let the next mount retry -- navigating away
      // would yank users out of unrelated flows.
      onError: (error) => {
        captureSentryError(error, {
          source: sentrySource,
          level: 'warning',
          tags: { experiment: 'Experiment' },
        });
      },
    });

  useFetchAllPages({
    data,
    hasNextPage,
    isFetchingNextPage,
    isError,
    enabled,
    fetchNextPage,
    onAllPagesLoaded: (loaded) => {
      // Shared-store guard: when several consumers mount concurrently, only
      // the first one to fire commits to the store. Subsequent instances
      // early-return so we don't write the same nodes N times.
      if (getIsQueried()) return;

      const campaigns = flattenNodes(loaded);
      setCampaignsByType(type, campaigns);
      setIsQueried(true);
      onLoaded?.(campaigns);
    },
  });

  // Throws on failure so callers decide recovery (fail-safe / toast /
  // ignore). Don't navigate from inside the hook — consumers mount in many
  // contexts (make-winner modal, reports, orders, ...).
  const handleRefetch = async (): Promise<void> => {
    try {
      const fresh = await refetchAllPages({
        queryClient,
        queryKey: useInfiniteCampaignTemplates.getKey(queryVariables),
        refetch,
        fetchNextPage,
        hasMorePages,
      });

      const campaigns = flattenNodes(fresh);
      setCampaignsByType(type, campaigns);
      setIsQueried(true);
      onLoaded?.(campaigns);
    } catch (error) {
      captureSentryError(error, {
        source: `${sentrySource}.handleRefetch`,
        level: 'warning',
        tags: { experiment: 'Experiment' },
      });
      throw error;
    }
  };

  return {
    // `hasNextPage` keeps `isFetching` true across the gap between a page
    // resolving and the next fetchNextPage firing in the effect.
    isFetching: isFetching || (!isError && !!hasNextPage),
    handleRefetch,
  };
};
